// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { z } from 'zod';

const isHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const bankLogoUrlSchema = z
  .string()
  .transform(value => value.trim())
  .refine(value => value === '' || isHttpUrl(value));

/**
 * Institution captured in the manual bank upload wizard.
 *
 * @remarks
 * UI form fields (camelCase). Maps to `AdvisoryAccountsCitecInstitution` via
 * `buildBulkUploadAccountData`. Backend `id` is assigned on upload; the form only
 * collects display fields.
 * @example
 *
 * ```ts
 * const institution = bankInstitutionSchema.parse({
 *   name: 'My Bank',
 *   logoUrl: 'https://cdn.example.com/my-bank.svg',
 * });
 * ```
 */
export const bankInstitutionSchema = z.object({
  name: z.string().trim().min(1),
  logoUrl: bankLogoUrlSchema,
});

export type BankInstitution = z.infer<typeof bankInstitutionSchema>;

/** Institution targeted by the manual bank update drawer. */
export const updateBankInstitutionSchema = bankInstitutionSchema.extend({
  id: z.string().min(1),
});

export type UpdateBankInstitution = z.infer<typeof updateBankInstitutionSchema>;

/** Institution picker on the select-existing wizard screen. */
export const bankInstitutionSelectExistingSchema = z.object({
  institutionId: z.string().min(1),
});

export type BankInstitutionSelectExisting = z.infer<
  typeof bankInstitutionSelectExistingSchema
>;
